import type { Artifact, ArtifactKind } from "./types";

/**
 * Blocks shorter than this stay inline in the transcript. Lifting a four-line
 * snippet into a side panel costs the reader more than it saves.
 */
export const ARTIFACT_MIN_LINES = 12;
export const ARTIFACT_MIN_CHARS = 380;

const LANGUAGE_ALIASES: Record<string, string> = {
  js: "javascript",
  jsx: "javascript",
  ts: "typescript",
  tsx: "typescript",
  py: "python",
  rb: "ruby",
  rs: "rust",
  sh: "bash",
  shell: "bash",
  zsh: "bash",
  yml: "yaml",
  md: "markdown",
  htm: "html",
  "c++": "cpp",
  "c#": "csharp",
  cs: "csharp",
  golang: "go",
  plaintext: "text",
  txt: "text",
};

const EXTENSIONS: Record<string, string> = {
  javascript: "js",
  typescript: "ts",
  python: "py",
  ruby: "rb",
  rust: "rs",
  bash: "sh",
  yaml: "yaml",
  markdown: "md",
  html: "html",
  css: "css",
  json: "json",
  sql: "sql",
  go: "go",
  java: "java",
  cpp: "cpp",
  csharp: "cs",
  php: "php",
  swift: "swift",
  kotlin: "kt",
  svg: "svg",
  mermaid: "mmd",
  text: "txt",
};

export function normalizeLanguage(raw: string | undefined): string {
  const value = (raw ?? "").trim().toLowerCase();
  if (!value) return "text";
  return LANGUAGE_ALIASES[value] ?? value;
}

export function extensionFor(language: string): string {
  return EXTENSIONS[normalizeLanguage(language)] ?? "txt";
}

/**
 * A React artifact is a JSX module that exports a component. It is classified
 * separately from plain TypeScript because it is previewable, not just readable.
 */
function looksLikeReact(language: string, content: string): boolean {
  if (!["javascript", "typescript"].includes(language)) return false;
  const exportsComponent =
    /export\s+default\s+(function|class|\(|[A-Z])/.test(content) ||
    /export\s+(function|const)\s+[A-Z]\w*/.test(content);
  const hasJsx = /<[A-Za-z][\w.]*[\s/>]/.test(content);
  return exportsComponent && hasJsx;
}

export function classifyArtifact(language: string, content: string): ArtifactKind {
  const normalized = normalizeLanguage(language);
  if (normalized === "svg" || /^\s*<svg[\s>]/i.test(content)) return "svg";
  if (normalized === "mermaid") return "mermaid";
  if (normalized === "html") return "html";
  if (normalized === "markdown") return "markdown";
  if (looksLikeReact(normalized, content)) return "react";
  return "code";
}

/** Kinds the artifact panel can render as something other than source text. */
export function isPreviewable(kind: ArtifactKind): boolean {
  return kind === "html" || kind === "svg" || kind === "markdown" || kind === "mermaid";
}

export interface DetectedBlock {
  language: string;
  content: string;
  /** Character offsets of the whole fenced block within the source message. */
  start: number;
  end: number;
}

const FENCE = /^([ \t]*)(`{3,}|~{3,})[ \t]*([\w+#.-]*)[ \t]*$/;

/**
 * Extracts top-level fenced code blocks. Written as a line scanner rather than
 * a single regex so that fences nested inside a longer fence (a markdown
 * artifact containing examples) do not terminate the outer block early.
 */
export function detectCodeBlocks(markdown: string): DetectedBlock[] {
  const blocks: DetectedBlock[] = [];
  const lines = markdown.split("\n");
  let offset = 0;
  let open: { fence: string; language: string; start: number; bodyFrom: number } | undefined;

  for (const line of lines) {
    const lineStart = offset;
    offset += line.length + 1;
    const match = FENCE.exec(line);
    if (!open) {
      if (match) {
        open = {
          fence: match[2]!,
          language: match[3] ?? "",
          start: lineStart,
          bodyFrom: offset,
        };
      }
      continue;
    }
    // Only a fence of the same character and at least the same length closes.
    const closes =
      match &&
      !match[3] &&
      match[2]!.startsWith(open.fence[0]!) &&
      match[2]!.length >= open.fence.length;
    if (closes) {
      blocks.push({
        language: normalizeLanguage(open.language),
        content: markdown.slice(open.bodyFrom, Math.max(open.bodyFrom, lineStart - 1)),
        start: open.start,
        end: offset,
      });
      open = undefined;
    }
  }
  return blocks;
}

export function qualifiesAsArtifact(block: DetectedBlock): boolean {
  const content = block.content.trim();
  if (!content) return false;
  // Shell transcripts and one-liners are reference, not documents.
  if (["bash", "text"].includes(block.language) && content.split("\n").length < 25) {
    return false;
  }
  return (
    content.split("\n").length >= ARTIFACT_MIN_LINES ||
    content.length >= ARTIFACT_MIN_CHARS
  );
}

const TITLE_PATTERNS: Array<RegExp> = [
  /^\s*(?:\/\/|#|--|;)\s*(?:filename|file)\s*[:=]\s*(.+)$/im,
  /^\s*<!--\s*(?:filename|file)\s*[:=]\s*(.+?)\s*-->$/im,
  /^#\s+(.+)$/m,
  /^\s*(?:export\s+default\s+)?(?:function|class)\s+([A-Z][\w]*)/m,
  /^\s*<title>(.+?)<\/title>/im,
];

export function inferTitle(block: DetectedBlock, fallbackIndex: number): string {
  for (const pattern of TITLE_PATTERNS) {
    const match = pattern.exec(block.content);
    const captured = match?.[1]?.trim();
    if (captured && captured.length <= 70) return captured;
  }
  const kind = classifyArtifact(block.language, block.content);
  const label =
    kind === "html"
      ? "Pagina"
      : kind === "svg"
        ? "Grafica"
        : kind === "markdown"
          ? "Documento"
          : kind === "mermaid"
            ? "Diagramma"
            : kind === "react"
              ? "Componente"
              : "Codice";
  return fallbackIndex > 0 ? `${label} ${fallbackIndex + 1}` : label;
}

export interface ExtractionResult {
  artifacts: Array<Omit<Artifact, "conversationId" | "revisions">>;
  /** The message body with extracted blocks replaced by a reference marker. */
  content: string;
}

/**
 * Lifts qualifying blocks out of an assistant reply and replaces each with a
 * marker the transcript renders as an artifact card. Non-qualifying blocks are
 * left untouched so that short snippets keep reading inline.
 */
export function extractArtifacts(
  message: { id: string; content: string },
  now = Date.now(),
): ExtractionResult {
  const blocks = detectCodeBlocks(message.content).filter(qualifiesAsArtifact);
  if (!blocks.length) return { artifacts: [], content: message.content };

  const artifacts: ExtractionResult["artifacts"] = [];
  let content = "";
  let cursor = 0;
  blocks.forEach((block, index) => {
    const id = crypto.randomUUID();
    const kind = classifyArtifact(block.language, block.content);
    artifacts.push({
      id,
      messageId: message.id,
      title: inferTitle(block, index),
      kind,
      language: kind === "svg" ? "svg" : normalizeLanguage(block.language),
      content: block.content.trim(),
      createdAt: now,
      updatedAt: now,
    });
    content += message.content.slice(cursor, block.start);
    content += `[[artifact:${id}]]\n`;
    cursor = block.end;
  });
  content += message.content.slice(cursor);
  return { artifacts, content: content.trim() };
}

export function artifactFileName(artifact: Pick<Artifact, "title" | "language">): string {
  const extension = extensionFor(artifact.language);
  // Titles inferred from a `// filename: server.ts` comment already carry the
  // extension; slugging it wholesale would yield "server-ts.ts".
  const stem = artifact.title.trim().toLowerCase().replace(/\.[a-z0-9]{1,5}$/, "");
  const base =
    stem
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "") || "artifact";
  return `${base}.${extension}`;
}

/**
 * Wraps artifact content into a standalone document for the preview frame.
 * Everything runs inside a sandboxed iframe with no same-origin access.
 */
export function previewDocument(artifact: Pick<Artifact, "kind" | "content">): string {
  if (artifact.kind === "svg") {
    return `<!doctype html><meta charset="utf-8"><style>
      html,body{margin:0;height:100%;display:grid;place-items:center;background:transparent}
      svg{max-width:100%;max-height:100%}
    </style>${artifact.content}`;
  }
  if (artifact.kind === "html") {
    // A fragment gets a document wrapper; anything that already declares a
    // doctype or an <html> root is passed through untouched.
    const head = artifact.content.trimStart().slice(0, 200).toLowerCase();
    return head.startsWith("<!doctype") || /<html[\s>]/.test(head)
      ? artifact.content
      : `<!doctype html><meta charset="utf-8">${artifact.content}`;
  }
  return "";
}
